import React from 'react';
import { motion } from 'framer-motion';
import { ResultsVsAverage, ResultsVsAverageMetric } from '../../types/analytics';
import { staggerContainer, staggerItem } from '@/lib/motion';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';

export interface ResultsVsAverageChartProps {
  data: ResultsVsAverage;
  loading?: boolean;
}

/**
 * Format a metric value for display next to its bar (e.g. "30 min", "72%", "3.5/5").
 * The unit "%" and "/5" hug the number; everything else gets a separating space.
 */
function formatValue(value: number | null, unit: string): string {
  if (value === null || value === undefined) {
    return '—';
  }
  const num = value.toString();
  // "%" and "/5" hug the number; other units (e.g. "min") get a separating space.
  if (unit === '%' || unit.startsWith('/')) {
    return `${num}${unit}`;
  }
  return `${num} ${unit}`;
}

/** A single value bar with its trailing label. `widthPct` is 0–100. */
function ValueBar({
  widthPct,
  color,
  label,
}: {
  widthPct: number;
  color: string;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0">
        <motion.div
          className={`h-2.5 rounded-full ${color}`}
          initial={{ width: 0 }}
          animate={{ width: `${widthPct}%` }}
          transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
          // A barely-visible sliver keeps zero-values legible.
          style={{ minWidth: widthPct > 0 ? undefined : '2px' }}
        />
      </div>
      <span className="body-small text-text-secondary whitespace-nowrap shrink-0">{label}</span>
    </div>
  );
}

function MetricRow({ metric }: { metric: ResultsVsAverageMetric }) {
  const learner = metric.learner ?? 0;
  const average = metric.average ?? 0;
  // Normalize each pair of bars within its own row, guarding against 0.
  const max = Math.max(learner, average, 0);
  const learnerPct = max > 0 ? (learner / max) * 100 : 0;
  const averagePct = max > 0 ? (average / max) * 100 : 0;

  return (
    <motion.div
      variants={staggerItem}
      className="grid grid-cols-[8rem,1fr] items-center gap-4 sm:grid-cols-[10rem,1fr]"
    >
      <p className="body-small text-text-secondary text-right">{metric.label}</p>
      <div className="flex flex-col gap-1.5">
        <ValueBar
          widthPct={learnerPct}
          color="bg-primary-600 dark:bg-primary-400"
          label={formatValue(metric.learner, metric.unit)}
        />
        <ValueBar
          widthPct={averagePct}
          color="bg-primary-300 dark:bg-primary-700"
          label={formatValue(metric.average, metric.unit)}
        />
      </div>
    </motion.div>
  );
}

const ResultsVsAverageChart: React.FC<ResultsVsAverageChartProps> = ({ data, loading = false }) => {
  const { theme } = useTheme();
  const learnerName = data?.learner?.name?.trim() || 'Their';
  const found = data?.learner?.found ?? false;
  const metrics = data?.metrics ?? [];

  const title = learnerName.endsWith('s')
    ? `${learnerName}' Results vs The Average of the Team`
    : `${learnerName}'s Results vs The Average of the Team`;

  return (
    <div className="card p-6" data-testid={loading ? 'results-vs-average-skeleton' : 'results-vs-average'}>
      <phantom-ui {...phantomProps(theme === 'dark', loading)}>
        {/* Title + legend */}
        <div className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="heading-3 text-text-primary">{title}</h3>
          <span className="flex items-center gap-3 ml-1">
            <span className="flex items-center gap-1.5 body-small text-text-secondary">
              <span className="w-2.5 h-2.5 rounded-full bg-primary-600 dark:bg-primary-400" aria-hidden="true" />
              Results
            </span>
            <span className="flex items-center gap-1.5 body-small text-text-secondary">
              <span className="w-2.5 h-2.5 rounded-full bg-primary-300 dark:bg-primary-700" aria-hidden="true" />
              Average
            </span>
          </span>
        </div>

        {!loading && !found ? (
          <p className="body-base text-text-tertiary text-center py-8">No data for this learner</p>
        ) : (
          <motion.div
            className="flex flex-col gap-5"
            variants={staggerContainer(0.05)}
            initial="hidden"
            animate="visible"
          >
            {metrics.map((metric) => (
              <MetricRow key={metric.key} metric={metric} />
            ))}
          </motion.div>
        )}
      </phantom-ui>
    </div>
  );
};

export default ResultsVsAverageChart;
